import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { authApi, adminApi } from '../utils/api';
import { log } from '../config/env';

interface User {
  name: string;
  email: string;
  phone: string;
  role?: string;
  permissions?: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  permissions: string[];
  login: (token: string, user: User, role?: string, permissions?: string[]) => void;
  logout: () => void;
  loading: boolean;
  justLoggedIn: boolean;
  checkAuthorization: () => Promise<boolean>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  clearJustLoggedIn: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  console.log('🔄 AuthProvider rendering');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedToken = localStorage.getItem('token');

        // Only require token - other fields are optional
        if (storedToken) {
          // For now, trust localStorage and don't make API calls on every mount
          // This prevents automatic logouts due to network issues or missing API endpoints
          log.info('Restoring authentication from localStorage');

          const storedName = localStorage.getItem('name') || 'User';
          const storedEmail = localStorage.getItem('email') || '';
          const storedPhone = localStorage.getItem('phone') || '';
          const storedRole = localStorage.getItem('role');
          const storedPermissions = localStorage.getItem('permissions');

          const parsedPermissions = storedPermissions ? JSON.parse(storedPermissions) : [];
          setToken(storedToken);
          setUser({
            name: storedName,
            email: storedEmail,
            phone: storedPhone,
            role: storedRole || undefined,
            permissions: parsedPermissions,
          });
          setPermissions(parsedPermissions);
          setIsAuthenticated(true);
        } else {
          // No stored token
          log.info('No stored authentication found');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // If there's an error (like JSON parsing), clear everything
        log.warn('Error parsing stored auth data, clearing');
        localStorage.clear();
        setToken(null);
        setUser(null);
        setPermissions([]);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Set up 401 redirect callback in API service (only once)
  useEffect(() => {
    const handleUnauthorized = () => {
      log.warn('API returned 401 Unauthorized, logging out user');
      // Clear localStorage
      localStorage.clear();
      // Clear API service token
      adminApi.clearToken();
      // Reset state
      setToken(null);
      setUser(null);
      setPermissions([]);
      setIsAuthenticated(false);
      // Redirect to login using window.location
      log.debug('Redirecting to login page');
      window.location.href = '/auth/login';
    };

    adminApi.setOnUnauthorized(handleUnauthorized);

    // Cleanup: remove callback on unmount
    return () => {
      adminApi.setOnUnauthorized(() => {});
    };
  }, []);

  const login = useCallback((newToken: string, newUser: User, role?: string, userPermissions?: string[]) => {
    try {
      // Store in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('name', newUser.name);
      localStorage.setItem('email', newUser.email);
      localStorage.setItem('phone', newUser.phone);
      if (role) localStorage.setItem('role', role);
      if (userPermissions) localStorage.setItem('permissions', JSON.stringify(userPermissions));

      // Update API service token
      adminApi.setToken(newToken);

      // Update state
      const updatedUser = {
        ...newUser,
        role,
        permissions: userPermissions || [],
      };
      setToken(newToken);
      setUser(updatedUser);
      setPermissions(userPermissions || []);
      setIsAuthenticated(true);
      setJustLoggedIn(true);
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call logout API endpoint
      const currentToken = token || localStorage.getItem('token');
      if (currentToken) {
        try {
          await adminApi.call('/auth/logout', 'POST');
        } catch (apiError) {
          // Continue with logout even if API call fails
          console.warn('Logout API call failed, continuing with local logout:', apiError);
        }
      }

      // Clear localStorage
      localStorage.clear();

      // Clear API service token
      adminApi.clearToken();

      // Reset state
      setToken(null);
      setUser(null);
      setPermissions([]);
      setIsAuthenticated(false);
      setJustLoggedIn(false);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [token]);

  const clearJustLoggedIn = useCallback(() => {
    setJustLoggedIn(false);
  }, []);

  const checkAuthorization = useCallback(async (): Promise<boolean> => {
    try {
      const currentToken = token || localStorage.getItem('token');

      if (!currentToken) {
        log.debug('No token available for authorization check');
        return false;
      }

      log.debug('Checking authorization with token:', currentToken.substring(0, 10) + '...');

      // Simple check: if token exists in localStorage, user is authorized
      // The actual token validation will happen when they make API calls
      // This prevents redirect loops due to network issues or API timeouts
      log.info('Token exists in localStorage, user is authorized');
      return true;
    } catch (error) {
      console.error('Error checking authorization:', error);
      // Don't automatically log out on errors, but return false to indicate failure
      log.warn('Authorization check error, keeping user logged in');
      return false;
    }
  }, [token]);

  // Permission checking functions
  const hasPermission = useCallback((permission: string): boolean => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((...permissionList: string[]): boolean => {
    return permissionList.some(permission => permissions.includes(permission));
  }, [permissions]);

  const hasAllPermissions = useCallback((...permissionList: string[]): boolean => {
    return permissionList.every(permission => permissions.includes(permission));
  }, [permissions]);

  const value: AuthContextType = useMemo(() => {
    console.log('🔄 AuthContext value recreated', {
      isAuthenticated,
      user: user?.name,
      token: token ? 'present' : 'null',
      permissions: permissions.length,
      loading,
      justLoggedIn
    });
    return {
      isAuthenticated,
      user,
      token,
      permissions,
      login,
      logout,
      loading,
      justLoggedIn,
      checkAuthorization,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      clearJustLoggedIn,
    };
  }, [
    isAuthenticated,
    user,
    token,
    permissions,
    login,
    logout,
    loading,
    justLoggedIn,
    checkAuthorization,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    clearJustLoggedIn,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
